<template>
  <div ref="contentContainer" class="bg-[#F6F4F0]">
    <div ref="itemsContainer">
      <!-- Active label -->
      <div class="flex items-end gap-[16px] mt-[24px]">
        <p class="flex-1 text-[0.875rem] leading-[1.25rem] font-inter font-semibold text-[#373531]">
          Active
        </p>
      </div>

      <!-- Empty State: Show when there are no recurring giving items -->
      <div
        v-if="hasNoRecurringGiving"
        class="flex items-center gap-[16px] px-[20px] py-[16px] rounded-[10px] border border-dashed border-[#A5A4A3] mt-[8px] cursor-pointer"
        @click="addRecurringGiving"
      >
        <div class="flex flex-col gap-[8px] items-start flex-1">
          <div class="flex gap-[8px] items-center w-full">
            <div class="w-[24px] h-[24px] shrink-0">
              <img src="assets/icons/plus.svg" alt="Plus icon" class="w-full h-full" />
            </div>
            <p class="text-[1rem] leading-[1.5rem] font-bold text-[#21201E] flex-1">
              Set up recurring giving
            </p>
          </div>
          <div class="pl-[32px] pr-0 py-0 w-full">
            <p class="text-[0.875rem] leading-[1.25rem] font-normal text-[#3D3A35]">
              Automate the important! Never miss a tithe or offering again.
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="activeRecurringGivingList.length > 0"
        class="flex flex-col gap-[12px] w-full mt-[24px]"
      >
        <div class="flex flex-col gap-[12px] w-full">
          <RadioItem
            v-for="option in activeRecurringGivingList"
            :key="option.id"
            :title="option.givingCategory"
            :bank-account="
              getBankAccount(option.egiro.applicantBankCode, option.egiro.applicantBankAccNo)
            "
            :frequency="getFrequencyText(option.frequency, option.startDate, option.dayOfWeek)"
            :amount="option.amount"
            :next-giving-date="getNextGivingDate(option.nextRecurringDate)"
            :show-chevron="true"
            @click="handleRecordClick(option)"
          />
        </div>
      </div>

      <div
        v-if="pendingRemovalRecurringGivingList.length > 0"
        class="flex flex-col gap-[12px] mt-[24px] w-full"
      >
        <p class="text-[0.875rem] leading-[1.25rem] font-inter font-semibold text-[#373531]">
          Pending to be removed
        </p>
        <div class="flex flex-col gap-[12px] w-full">
          <RadioItem
            v-for="option in pendingRemovalRecurringGivingList"
            :key="option.id"
            :title="option.givingCategory"
            :bank-account="
              getBankAccount(option.egiro.applicantBankCode, option.egiro.applicantBankAccNo)
            "
            :frequency="getFrequencyText(option.frequency, option.startDate, option.dayOfWeek)"
            :amount="option.amount"
            :next-giving-date="getNextGivingDate(option.nextRecurringDate)"
            :is-pending-removal="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import RadioItem from '@/components/MyGiving/RecurringGivingPage/RadioItem.vue';
import BottomButton from '@/components/common/BottomButton.vue';
import { BANK_ENUM } from '@/constants/MyGiving/bank-list';

export default defineComponent({
  name: 'RadioListPage',
  components: {
    RadioItem,
    BottomButton,
  },
  props: {
    recurringGivingList: {
      type: Array,
      required: true,
      default: () => [],
    },
    hideButtons: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const contentContainer = ref(null);
    const itemsContainer = ref(null);

    const getBankAccount = (applicantBankCode, applicantBankAccNo) => {
      if (applicantBankCode && applicantBankAccNo) {
        // Get bank short name from BANK_ENUM using the bank code
        const bankInfo = BANK_ENUM[applicantBankCode];
        const bankName = bankInfo?.name || applicantBankCode;
        const lastFour = applicantBankAccNo.slice(-4);
        return `${bankName} Account ${lastFour}`;
      }
      return '';
    };

    const getFrequencyText = (frequency, startDate, dayOfWeek) => {
      if (frequency === 'MONTHLY') {
        const day = new Date(startDate).getUTCDate();
        return `Monthly, on the ${day}${getDaySuffix(day)}`;
      } else if (frequency === 'WEEKLY') {
        return `Weekly, on ${dayOfWeek || 'Sunday'}`;
      }
      return frequency || '';
    };

    const getDaySuffix = day => {
      if (day >= 11 && day <= 13) return 'th';
      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    const getSubtitle2 = (applicantBankCode, applicantBankAccNo) => {
      if (applicantBankCode && applicantBankAccNo) {
        const lastFourDigits = applicantBankAccNo.toString().slice(-4);
        return `${lastFourDigits}`;
      } else {
        return '';
      }
    };

    const getNextGivingDate = nextRecurringDate => {
      if (!nextRecurringDate) return '';

      // Use the nextRecurringDate directly from the API
      const nextDate = new Date(nextRecurringDate);

      // Format as "DD MMM YYYY" (e.g., "21 Dec 2025")
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const day = nextDate.getUTCDate();
      const month = months[nextDate.getUTCMonth()];
      const year = nextDate.getUTCFullYear();
      return `${day} ${month} ${year}`;
    };

    const activeRecurringGivingList = computed(() => {
      return props.recurringGivingList.filter(item => {
        // Filter for active items (not pending removal)
        return !item.status || item.status === 'ACTIVE';
      });
    });

    const pendingRemovalRecurringGivingList = computed(() => {
      return props.recurringGivingList.filter(item => {
        // Filter for items pending removal
        return item.status === 'PENDING_REMOVAL';
      });
    });

    const hasNoRecurringGiving = computed(() => {
      return (
        activeRecurringGivingList.value.length === 0 &&
        pendingRemovalRecurringGivingList.value.length === 0
      );
    });

    return {
      contentContainer,
      itemsContainer,
      getBankAccount,
      getFrequencyText,
      getNextGivingDate,
      getDaySuffix,
      activeRecurringGivingList,
      pendingRemovalRecurringGivingList,
      hasNoRecurringGiving,
    };
  },
  methods: {
    navigateTo(route) {
      this.$router.push(route);
    },
    handleRecordClick(record) {
      this.manageRecurringGiving(record);
    },
    manageRecurringGiving(selectData) {
      if (!selectData) return;

      this.$store.commit('myGiving/setGivingType', selectData.givingCategory);
      this.$store.commit('myGiving/setGivingMethod', selectData.egiro.applicantBankCode);
      this.$store.commit(
        'myGiving/setGivingAccount',
        `**** ***` + selectData.egiro.applicantBankAccNo.slice(-4),
      );
      this.$store.commit(
        'myGiving/setAvailableLimitForSelectedAccount',
        Number(selectData.egiro.maxAmount),
      );
      this.$store.commit('myGiving/setIsFromGivenNow', true);
      // The FAST page charges the store's mandate; editing a recurring gift used to
      // pass it only in the query, leaving the store's value from an earlier visit.
      this.$store.commit('myGiving/setEGiroMandateId', selectData.egiroRef);

      this.$router.push({
        path: '/my-giving/local-transfer-egiro',
        query: {
          recurringId: selectData.id,
          egiroRef: selectData.egiroRef,
          amount: selectData.amount,
          currency: selectData.currency,
          frequency:
            selectData.frequency.charAt(0).toUpperCase() +
            selectData.frequency.slice(1).toLowerCase(),
          startDate: selectData.startDate,
          endDate: null,
          givingCategory: selectData.givingCategory,
          paymentDetails: 'Regular monthly giving',
          dayOfWeek: selectData.dayOfWeek,
        },
      });
    },
    async addRecurringGiving() {
      // Check if linked accounts are already loaded
      let linkedAccounts = this.$store.getters['myGiving/linkedBankAccounts'];

      // If not loaded or empty, fetch them
      if (!linkedAccounts || linkedAccounts.length === 0) {
        await this.$store.dispatch('myGiving/getLinkedBankAccounts');
        linkedAccounts = this.$store.getters['myGiving/linkedBankAccounts'];
      }

      // Check if user has linked account
      const hasLinkedAccount = linkedAccounts && linkedAccounts.length > 0;

      if (hasLinkedAccount) {
        // User has linked account - go directly to giving options page with fromrecurring query
        this.$router.push({ path: '/my-giving/giving-options', query: { fromrecurring: true } });
      } else {
        // User doesn't have linked account - go to setup page which will show the modal
        this.$router.push('/my-giving/setup-recurring-start');
      }
    },
  },
});
</script>
